/**
 * State-agnostic election data layer.
 * Adding Negeri Sembilan or a GE later = register a new ElectionDataset; no UI rewrite.
 */
import johorDun2022 from "@/data/johor_dun_2022.json";
import johorParl2022 from "@/data/johor_parliament_2022.json";
import johorDunGeo from "@/data/johor_dun.geojson?url";
import johorParlGeo from "@/data/johor_parliament.geojson?url";
import { johorDemographics, StateDemographics } from "@/data/demographics";

export interface Candidate {
  name: string;
  party: string;
  sub: string;
  votes: number;
  pct: number;
}

export interface DunResult {
  code: string;
  name: string;
  parliament: string;
  registered: number;
  valid: number;
  winner: string;
  party: string;
  sub: string;
  votes: number;
  pct: number;
  majority: number;
  candidates: Candidate[];
}

export interface ParliamentResult {
  code: string;
  name: string;
  winner: string;
  party: string;
  sub: string;
  majority: number;
  majority_pct: number;
  registered: number;
  votes_cast: number;
  turnout: number;
  rejected: number;
  rejected_pct: number;
}

export interface ElectionDataset {
  id: string;
  state: string;
  label: string;
  status: "available" | "upcoming";
  resultYear: number;
  upcoming?: {
    name: string;
    dissolved: string;
    nomination: string;
    earlyVoting: string;
    polling: string;
    totalSeats: number;
    summary: string;
  };
  geo: { dun: string; parliament: string };
  dun: DunResult[];
  parliament: ParliamentResult[];
  demographics: StateDemographics;
  totalDun: number;
  totalParliament: number;
}

export const JOHOR: ElectionDataset = {
  id: "johor",
  state: "Johor",
  label: "Johor",
  status: "available",
  resultYear: 2022,
  upcoming: {
    name: "16th Johor State Election",
    dissolved: "1 June 2026",
    nomination: "27 June 2026",
    earlyVoting: "7 July 2026",
    polling: "11 July 2026",
    totalSeats: 56,
    summary:
      "Johor's state assembly was dissolved on 1 June 2026, triggering the state's 16th election. Barisan Nasional — which won 40 of 56 seats in 2022 — is defending a two-thirds majority, while federal partners Pakatan Harapan and the opposition Perikatan Nasional contest separately. The vote is widely viewed as a national bellwether.",
  },
  geo: { dun: johorDunGeo, parliament: johorParlGeo },
  dun: johorDun2022 as DunResult[],
  parliament: johorParl2022 as ParliamentResult[],
  demographics: johorDemographics,
  totalDun: 56,
  totalParliament: 26,
};

// Negeri Sembilan placeholder — data to be added in the next rollout.
export const NEGERI_SEMBILAN_PLACEHOLDER = {
  id: "negerisembilan",
  state: "Negeri Sembilan",
  label: "Negeri Sembilan",
  status: "upcoming" as const,
  totalDun: 36,
  totalParliament: 8,
};

export const DATASETS: ElectionDataset[] = [JOHOR];

export function getDataset(id: string): ElectionDataset | undefined {
  return DATASETS.find((d) => d.id === id);
}

// ---- Aggregations ----
export interface SeatTally {
  party: string;
  seats: number;
}

export function tallyDun(ds: ElectionDataset): SeatTally[] {
  const m = new Map<string, number>();
  ds.dun.forEach((d) => m.set(d.party, (m.get(d.party) ?? 0) + 1));
  return Array.from(m.entries())
    .map(([party, seats]) => ({ party, seats }))
    .sort((a, b) => b.seats - a.seats);
}

export function tallyParliament(ds: ElectionDataset): SeatTally[] {
  const m = new Map<string, number>();
  ds.parliament.forEach((d) => m.set(d.party, (m.get(d.party) ?? 0) + 1));
  return Array.from(m.entries())
    .map(([party, seats]) => ({ party, seats }))
    .sort((a, b) => b.seats - a.seats);
}

export function dunByParliament(ds: ElectionDataset, parlCode: string): DunResult[] {
  return ds.dun.filter((d) => d.parliament === parlCode);
}

export function statewideTurnout(ds: ElectionDataset): number {
  const reg = ds.parliament.reduce((s, p) => s + p.registered, 0);
  const cast = ds.parliament.reduce((s, p) => s + p.votes_cast, 0);
  return reg ? (cast / reg) * 100 : 0;
}
